import { useMemo, useState } from 'react'

import { RoleStatus } from '@b-stock/account-api-client'
import type { PlaceBidBatchBodyInner } from '@b-stock/auction-api-client'
import {
  useAccessToken,
  useAccessTokenRoles,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import {
  BidStatus,
  getAuctionWithBid,
  getBidsClient,
  getEligibilityClient,
  getQuotesClient,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { ShipmentType } from '@b-stock/shipment-api-client'

import { getUserMinBidAmount } from '@/helpers/getUserMinBidAmount'
import type { GaSessionIdPayload } from '@/helpers/telemetry/gaSessionIds'
import { useGaSessionIdPayload } from '@/helpers/telemetry/useGaSessionIdPayload'

import type { BulkBiddableListing } from './types'

/**
 * A listing this hook itself filtered out before ever calling placeBidBatch
 * — as opposed to a real batch-API failure, which only has free-text
 * (errorMessage) available. Coded so callers can localize instead of
 * showing raw English (this hook has no react-intl access of its own,
 * and is shared by callers — BulkBidModal, MyBidsLayout,
 * WatchlistBidCardList — that don't read this field at all today).
 */
export type BidPlacementReasonCode =
  | 'alreadyWon'
  | 'notBiddable'
  | 'noActiveAuction'

/**
 * Per-listing outcome of a submit() call, for every listing passed in —
 * not just the ones that actually reach placeBidBatch. Lets a caller (e.g.
 * the Bid Sheet Upload Tool, GLOB-4879) show a result row for every listing
 * it submitted, instead of only an aggregate success/failure count.
 */
export type BidPlacementResult = {
  listingId: string
  success: boolean
  winning: boolean
  winningBidAmount?: number
  /**
   * The auction's next minimum bid, as of right after this placement —
   * i.e. no longer the stale pre-placement snapshot a caller (e.g. the Bid
   * Sheet Upload Tool's After-Upload Report, GLOB-4879) computed before
   * bidding. Undefined for rows that never reached placeBidBatch.
   */
  nextMinBidAmount?: number
  /** Same as nextMinBidAmount, but accounting for this bidder's own current position. */
  nextUserMinBidAmount?: number
  bidId?: string
  /** Raw batch-API failure text (PlaceBidBatchResponseInner.error) — not localizable, shown as-is. */
  errorMessage?: string
  /** Set instead of errorMessage for listings this hook pre-filtered — see BidPlacementReasonCode. */
  reasonCode?: BidPlacementReasonCode
}

type UseBulkBidMutationsOptions = {
  /** Defaults to getUserMinBidAmount — pass to bid a caller-specified amount instead. */
  getAmount?: (listing: BulkBiddableListing) => number
}

// Caps concurrent live bid-status lookups per submit() call — a large
// mixed selection could otherwise fail placement for many listings at
// once (e.g. a stale bid sheet upload), each triggering its own re-check.
const LIVE_STATUS_CHECK_CHUNK_SIZE = 20

const mapWithConcurrencyLimit = async <T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> => {
  const results: R[] = []
  for (let i = 0; i < items.length; i += limit) {
    const chunk = items.slice(i, i + limit)
    results.push(...(await Promise.all(chunk.map(fn))))
  }
  return results
}

/**
 * Only called for listings placeBidBatch just rejected — checks whether
 * that's because this buyer already holds the top spot, by reading the
 * auction service directly (not the active-listings search index a
 * caller's `listings` prop is typically built from, and not the batch
 * response either — placeBidBatch's rejection doesn't reliably say so on
 * its own). `listings` can be a snapshot fetched well before the buyer
 * clicks Submit — e.g. the Bid Sheet Upload Tool's results screen has no
 * time limit — so a listing this buyer has since taken the lead on (an
 * open auction, BidStatus.WINNING — not necessarily WON, which means the
 * auction has already ended) looks biddable right up until placement, and
 * a submitted amount below their own now-standing bid comes back as a
 * generic "amount below minimum" failure instead of Winning (GLOB-4879 QA
 * failure — the buyer's own repro used an open auction: "You're winning
 * this auction", not an ended one). Reporting Winning here is correct
 * regardless of why this specific attempt failed — if the buyer already
 * holds the top spot, that's the true, buyer-relevant state to show.
 */
const isAlreadyWinning = async (
  listingId: string,
  accessToken: string | null,
  accountId: string | null
): Promise<boolean> => {
  try {
    const auction = await getAuctionWithBid({
      accessToken,
      accountId,
      listingId,
    })
    // getAuctionWithBid returns Auction | LimitedAuction; only Auction
    // carries bidStatus, and only LimitedAuction carries `winning` (see
    // the same union handling in useBidCardRealtimeUpdates.ts).
    return (
      ('winning' in auction && auction.winning) ||
      auction.bidStatus === BidStatus.WON ||
      auction.bidStatus === BidStatus.WINNING
    )
  } catch (error) {
    // A failed re-check must never override a real result with a guess —
    // keep the batch API's own (already real) failure outcome as-is.
    console.warn(
      '[useBulkBidMutations] live bid-status re-check failed after a placement failure, keeping the original failure result',
      error
    )
    return false
  }
}

/** One call per listing, so cap how many run at once. */
const ELIGIBILITY_CHECK_CHUNK_SIZE = 8

type BatchQuoteItem = {
  listingId: string
  destinationLocationId: string
  shipmentType: ShipmentType
  accountId?: string
}

/**
 * Quote items for only the eligible listings, at the shipment type eligibility
 * reports: `allowedShipmentTypes` is what the listing permits, not what this
 * destination gets, and quoting on the wrong one lands a different fee.
 */
const buildEligibleQuoteItems = async ({
  accessToken,
  accountId,
  destinationLocationId,
  listings,
}: {
  accessToken: string | null
  accountId: string | null
  destinationLocationId: string
  listings: BulkBiddableListing[]
}): Promise<BatchQuoteItem[]> => {
  const eligibilityClient = getEligibilityClient()

  const items = await mapWithConcurrencyLimit(
    listings,
    ELIGIBILITY_CHECK_CHUNK_SIZE,
    async ({ listingId }): Promise<BatchQuoteItem | null> => {
      try {
        const { data } = await eligibilityClient.checkLocationEligibility(
          { listingId, locationId: [destinationLocationId] },
          buildAxiosRequestConfig({ accessToken })
        )
        const eligibility = data[0]
        const shipmentType = eligibility?.eligibleShipmentTypes[0] as
          | ShipmentType
          | undefined
        // No eligible type is not a quotable state; a default would be a guess.
        if (!eligibility?.isEligible || !shipmentType) return null
        return {
          listingId,
          destinationLocationId,
          shipmentType,
          ...(accountId && { accountId }),
        }
      } catch (error) {
        console.warn(
          `[useBulkBidMutations] eligibility check failed for ${listingId}, skipping its quote`,
          error
        )
        return null
      }
    }
  )

  return items.filter((i): i is BatchQuoteItem => i !== null)
}

export function useBulkBidMutations(
  listings: BulkBiddableListing[],
  options?: UseBulkBidMutationsOptions
) {
  const getAmount = options?.getAmount ?? getUserMinBidAmount
  const accessToken = useAccessToken()
  const accountId = useDecodedAccessToken()?.accountId ?? null
  const getGaSessionIdPayload = useGaSessionIdPayload()
  // The eligibility endpoint only permits ACTIVE/READ_ONLY buyers and non-buyer
  // service tokens; without eligibility there is no safe type to quote on.
  const buyerRole = useAccessTokenRoles()?.buyer
  const mayCheckEligibility =
    !buyerRole ||
    buyerRole === RoleStatus.Active ||
    buyerRole === RoleStatus.ReadOnly

  const { data: locationsData } = myLocationsQuery.useWithOpts(
    [
      {
        attributes: ['DEFAULT_DELIVERABLE'],
        accountId: accountId ? [accountId] : [],
      },
    ],
    { enabled: !!accountId }
  )
  const defaultLocation = locationsData?.locations[0]

  const biddable = useMemo(
    () =>
      listings.filter(
        (l) => l.bidStatus !== BidStatus.WON && l.nextMinBidAmount !== null
      ),
    [listings]
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async () => {
    setIsSubmitting(true)
    try {
      // Listings filtered out of `biddable` (already WON, or not currently
      // biddable at all) never reach placeBidBatch, but still need a result
      // so a caller showing one row per submitted listing never has a gap.
      const notBiddableResults: BidPlacementResult[] = listings
        .filter(
          (l) => l.bidStatus === BidStatus.WON || l.nextMinBidAmount === null
        )
        .map((l) => ({
          listingId: l.listingId,
          success: false,
          winning: l.bidStatus === BidStatus.WON,
          reasonCode:
            l.bidStatus === BidStatus.WON ? 'alreadyWon' : 'notBiddable',
        }))

      if (biddable.length === 0) {
        return {
          failedListingIds: [],
          total: 0,
          results: notBiddableResults,
        }
      }

      // Phase 1: batch quote selection, but only for listings that don't
      // already have a buyer-selected quote (e.g. Buyer Arranged) — requesting
      // one unconditionally would overwrite that selection with a new quote
      // against defaultLocation, same as the single-bid flow avoids via
      // getSelectedQuoteQuery in useAutoQuoteGeneration.
      if (defaultLocation && mayCheckEligibility) {
        try {
          const quotesClient = getQuotesClient()
          const listingIds = biddable.map((l) => l.listingId)
          const { data: existingQuotesData } = await quotesClient.findAllQuotes(
            {
              listingId: listingIds,
              accountId: accountId ? [accountId] : undefined,
              selected: true,
              limit: listingIds.length,
            },
            buildAxiosRequestConfig({ accessToken })
          )
          const listingIdsWithSelectedQuote = new Set(
            (existingQuotesData.quotes ?? [])
              .filter((q) => q.selected)
              .map((q) => q.listingId)
          )
          const listingsNeedingQuote = biddable.filter(
            (l) => !listingIdsWithSelectedQuote.has(l.listingId)
          )

          const quoteItems =
            listingsNeedingQuote.length > 0
              ? await buildEligibleQuoteItems({
                  accessToken,
                  accountId,
                  destinationLocationId: defaultLocation.id,
                  listings: listingsNeedingQuote,
                })
              : []

          if (quoteItems.length > 0) {
            await quotesClient.handleBatchQuotes(
              { createBatchQuotesDto: { items: quoteItems } },
              buildAxiosRequestConfig({ accessToken })
            )
          }
        } catch (error) {
          // Covers all three quote calls — findAllQuotes, the eligibility
          // loop, and handleBatchQuotes — so it must not name just one.
          console.warn(
            '[useBulkBidMutations] quote phase failed, proceeding with bids',
            error
          )
        }
      }

      // Phase 2: batch call — listings without an auctionId are pre-failed
      const batchable = biddable.filter(
        (l): l is BulkBiddableListing & { auctionId: string } =>
          l.auctionId !== null
      )
      const preFailed = biddable.filter((l) => l.auctionId === null)
      const preFailedIds = preFailed.map((l) => l.listingId)
      const preFailedResults: BidPlacementResult[] = preFailed.map((l) => ({
        listingId: l.listingId,
        success: false,
        winning: false,
        reasonCode: 'noActiveAuction',
      }))

      if (batchable.length === 0) {
        return {
          failedListingIds: preFailedIds,
          total: preFailedIds.length,
          bidIds: [],
          results: [...notBiddableResults, ...preFailedResults],
        }
      }

      const bidsClient = getBidsClient()
      // GLOB-4954: read the GA cookies once per submission and forward the
      // client/session ids on each batch item. The auction batch schema accepts
      // the two optional camelCase fields per array element (client regen follows
      // the backend capture branch mg-GLOB-4949-capture-ga-ids), so we extend the
      // item type locally. The payload is `{}` unless the kill switch is on AND
      // the GA cookies are present, in which case each item is identical to before.
      const gaSessionIdPayload = getGaSessionIdPayload()
      const response = await bidsClient.placeBidBatch(
        {
          placeBidBatchBodyInner: batchable.map(
            (l): PlaceBidBatchBodyInner & GaSessionIdPayload => ({
              auctionId: l.auctionId,
              amount: getAmount(l),
              ...gaSessionIdPayload,
            })
          ),
        },
        buildAxiosRequestConfig({ accessToken })
      )
      const auctionToListing = new Map(
        batchable.map((l) => [l.auctionId, l.listingId])
      )
      if (auctionToListing.size !== batchable.length) {
        console.warn(
          '[useBulkBidMutations] duplicate auctionId in selection — some failure reports may be lost'
        )
      }
      const bidIds = response.data
        .filter((r) => r.success && r.bidId)
        .map((r) => r.bidId as string)
      const rawBatchResults: BidPlacementResult[] = response.data
        .map((r): BidPlacementResult | null => {
          const listingId = auctionToListing.get(r.auctionId)
          return listingId
            ? {
                listingId,
                success: r.success,
                winning: r.winning ?? false,
                winningBidAmount: r.winningBidAmount,
                nextMinBidAmount: r.nextMinBidAmount,
                nextUserMinBidAmount: r.nextUserMinBidAmount,
                bidId: r.bidId,
                errorMessage: r.error,
              }
            : null
        })
        .filter((r): r is BidPlacementResult => r !== null)

      // A rejection here can mean either a genuine batch-API failure
      // (outbid, ended, etc.) or that this buyer already holds the winning
      // bid and their submitted amount is below the now-higher current
      // minimum — placeBidBatch doesn't reliably distinguish the two on
      // its own (GLOB-4879 QA failure). Only the actual failures get a
      // live re-check, so the far more common winning/losing outcomes
      // never pay for one.
      const unconfirmedFailures = rawBatchResults.filter(
        (r) => !r.success && !r.winning
      )
      const freshlyWonFlags = await mapWithConcurrencyLimit(
        unconfirmedFailures,
        LIVE_STATUS_CHECK_CHUNK_SIZE,
        (r) => isAlreadyWinning(r.listingId, accessToken, accountId)
      )
      const freshlyWonListingIds = new Set(
        unconfirmedFailures
          .filter((_, i) => freshlyWonFlags[i])
          .map((r) => r.listingId)
      )
      const batchResults: BidPlacementResult[] = rawBatchResults.map(
        (r): BidPlacementResult =>
          freshlyWonListingIds.has(r.listingId)
            ? {
                listingId: r.listingId,
                success: false,
                winning: true,
                reasonCode: 'alreadyWon',
              }
            : r
      )
      const failedListingIds = [
        ...preFailedIds,
        ...batchResults
          .filter((r) => !r.success && !freshlyWonListingIds.has(r.listingId))
          .map((r) => r.listingId),
      ]
      return {
        failedListingIds,
        total: batchable.length + preFailedIds.length,
        bidIds,
        results: [...notBiddableResults, ...preFailedResults, ...batchResults],
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    submit,
    isPending: isSubmitting,
    biddableCount: biddable.length,
    biddableListings: biddable,
  }
}
